import React, { useRef, useState, useEffect, useCallback } from 'react';
import CategoryPlaceholder from '/asset/box.png';
import { Category } from '../interfaces';

interface CategoriesListProps {
  sortedCategories: Category[];
  categoryIds: number[];
  setCategoryIds: React.Dispatch<React.SetStateAction<number[]>>;
  showFilters: boolean;
}

export default function CategoriesList({
  sortedCategories,
  categoryIds,
  setCategoryIds,
  showFilters,
}: CategoriesListProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeft, setShowLeft] = useState(false);
  const [showRight, setShowRight] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);

  const updateArrows = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setShowLeft(el.scrollLeft > 0);
    setShowRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
    setIsOverflowing(el.scrollWidth > el.clientWidth);
  }, []);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    updateArrows();
    el.addEventListener('scroll', updateArrows);

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateArrows);
      return () => {
        el.removeEventListener('scroll', updateArrows);
        window.removeEventListener('resize', updateArrows);
      };
    }

    const ro = new ResizeObserver(updateArrows);
    ro.observe(el);

    return () => {
      el.removeEventListener('scroll', updateArrows);
      ro.disconnect();
    };
  }, [updateArrows, sortedCategories]);

  return (
    <div
      className={`${showFilters ? 'bg-white' : ''} lg:bg-transparent lg:px-0`}
    >
      <div>
        <div
          className={`w-full max-w-[1250px]
            mx-auto relative ${showFilters ? 'block' : 'hidden lg:block'}`}
        >
          <div
            className={`pointer-events-none absolute inset-y-0 left-0 w-[100px] bg-gradient-to-l from-transparent to-white z-30 flex items-center justify-start pl-2 transition-opacity duration-200 ${showLeft ? 'opacity-100' : 'opacity-0'}`}
          >
            <svg
              className="w-5 h-5 text-gray-400 opacity-60"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </div>
          <div className="">
            <div
              ref={scrollRef}
              data-cy="category-list"
              className={`w-full overflow-x-auto !overflow-y-auto
              hide-scrollbar md:gap-[5px] flex ${isOverflowing ? 'justify-start' : 'justify-center'}`}
            >
              {sortedCategories.map((category, index: number) => (
                <div
                  className="
                md:w-[150px] p-[10px]"
                  key={category.id}
                >
                  <div
                    data-cy="category-item"
                    data-name={category.name}
                    className={`text-gray-900
                    relative
                    `}
                    key={index}
                    onClick={() => {
                      setCategoryIds((prev) =>
                        prev.includes(category.id)
                          ? prev.filter((id) => id !== category.id)
                          : [...prev, category.id]
                      );
                    }}
                  >
                    <div
                      className={`
                        min-w-[80px] h-[70px]
                        md:w-[140px] md:h-[100px] rounded-xl 
                        cursor-pointer bg-secondary-50 flex items-center 
                        border-[1px] border-secondary-50 border-solid
                        justify-center relative bg-white ${
                          categoryIds.includes(category.id)
                            ? 'border-[3px] border-secondary-500'
                            : ''
                        }`}
                    >
                      <img
                        src={category.coverImage || CategoryPlaceholder}
                        alt={category.name}
                        className="w-full h-full rounded-[10px]
                        object-cover object-center"
                      />
                    </div>
                    <div
                      className="mt-[10px] text-center 
                    text-[12px] text-white font-medium
                    md:absolute bottom-[10px] left-[10px] 
                    bg-secondary-500 px-[5px] py-[3px]
                    rounded-md font-dmsans"
                    >
                      {category.name}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div
            className={`pointer-events-none absolute inset-y-0 right-0 w-[80px] bg-gradient-to-r from-transparent to-white flex items-center justify-end pr-2 transition-opacity duration-200 ${showRight ? 'opacity-100' : 'opacity-0'}`}
          >
            <svg
              className="w-5 h-5 text-gray-400 opacity-60"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 5l7 7-7 7"
              />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}
